import React, { Component } from 'react'
import { translate } from 'react-polyglot'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import TextareaAutosize from 'react-textarea-autosize'
import { Editor as SlateEditor } from 'slate-react'
import { Value } from 'slate'
import { revisionActions } from 'features/revisions'
import moment from 'moment'
import renderBlock from 'components/Editor/Renderer/renderBlock'
import renderInline from 'components/Editor/Renderer/renderInline'
import renderMark from 'components/Editor/Renderer/renderMark'
import schema from 'components/Editor/Schema'
import history from 'configureHistory'

import {
	PageOverlay,
	RelativeTime,
	Table,
	Text,
	Icon,
	FlexContainer,
	Avatar,
	Button,
	Confirm,
} from 'components'

const emptyData = {
	object: 'value',
	document: {
		object: 'document',
		data: {},
		nodes: [
			{
				object: 'block',
				type: 'paragraph',
				data: {},
				nodes: [
					{
						object: 'text',
						text: '',
					},
				],
			},
		],
	},
}

class EditingHistoryPage extends Component {
	constructor(props) {
		super(props)

		this.state = {
			preview: null,
		}
	}

	componentDidMount() {
		const { articleId, getRevisions } = this.props

		getRevisions(articleId)
	}

	openPreview = id => {
		const { getRevision } = this.props

		getRevision(id)
		this.setState({ preview: 1 })
	}

	closePreview = () => {
		this.setState({ preview: null })
	}

	getValidTime = time => {
		const d = new Date()

		if (time >= moment()) {
			return d.setSeconds(d.getSeconds() - 1)
		}

		return time
	}

	renderPreview = () => {
		const { revision, t } = this.props

		if (!revision.updatedAt) return <div />

		return (
			<>
				<TextareaAutosize
					className="o-entry__title"
					readOnly
					value={
						revision.title && revision.title.length
							? revision.title
							: t('without_title')
					}
					minRows={1}
				/>
				<SlateEditor
					className="o-editor o-editor--read-only"
					readOnly
					value={
						revision.content
							? Value.fromJSON(JSON.parse(revision.content))
							: Value.fromJSON(emptyData)
					}
					// renderNode={renderNode}
					renderMark={renderMark}
					renderBlock={renderBlock}
					renderInline={renderInline}
					schema={schema}
				/>
			</>
		)
	}

	renderOverview = () => {
		const { revisions, title, t } = this.props

		return (
			<>
				<h2>
					{t('editing_history')}
					<Text size="small" icon={<Icon name="entry" />} inline>
						{title && title.length ? title : t('without_title')}
					</Text>
				</h2>
				<Text color="dark">{t('editing_history_text')}</Text>
				{revisions.length ? (
					<Table
						classNameModifier="editing-history"
						scrollOnMobile={false}
						data={revisions}
						columns={[
							{
								accessor: 'updated',
								className: 'has-border-right',
								Cell: props => (
									<FlexContainer justifyContent="flex-start">
										<Avatar
											image={
												props.original.updatedBy.image
											}
											initials={
												props.original.updatedBy.firstName.charAt(
													0
												) +
												props.original.updatedBy.lastName.charAt(
													0
												)
											}
										/>
										<Text color="dark" capitalize>
											<RelativeTime
												time={this.getValidTime(
													props.original.updatedAt
												)}
											/>
											<br />
											<small className="u-max-width--small">
												{
													props.original.updatedBy
														.firstName
												}{' '}
												{
													props.original.updatedBy
														.lastName
												}{' '}
												·{' '}
												{moment(
													this.getValidTime(
														props.original.updatedAt
													)
												).format(
													t('date_and_time_format')
												)}
											</small>
										</Text>
									</FlexContainer>
								),
							},
							{
								accessor: 'meta',
								className: 'centered',
								Cell: props => (
									<FlexContainer justifyContent="flex-start">
										<Text color="dark">
											{props.original.words}
											<br />
											<small>{t('words')}</small>
										</Text>
										<Text color="dark">
											{props.original.characters}
											<br />
											<small>{t('characters')}</small>
										</Text>
									</FlexContainer>
								),
							},
							{
								accessor: 'actions',
								sortable: false,
								Cell: props => (
									<FlexContainer
										justifyContent="flex-start"
										wrap
									>
										<Button
											text={t('preview')}
											size="small"
											layout="ghosted"
											onClick={() =>
												this.openPreview(
													props.original.id
												)
											}
										/>
										<Confirm
											title={t('restore_revision')}
											text={
												<>
													<Text size="small">
														{t(
															'restore_revision_long'
														)}
													</Text>
													<Text color="dark">
														{props.original.title &&
														props.original.title
															.length
															? props.original
																	.title
															: t(
																	'without_title'
															  )}
														<br />
														<small>
															{
																props.original
																	.updatedBy
																	.firstName
															}{' '}
															{
																props.original
																	.updatedBy
																	.lastName
															}{' '}
															·{' '}
															{moment(
																this.getValidTime(
																	props
																		.original
																		.updatedAt
																)
															).format(
																t(
																	'date_and_time_format'
																)
															)}
														</small>
													</Text>
													<Text size="small">
														{t(
															'restore_revision_notice'
														)}
													</Text>
												</>
											}
										>
											{confirm => (
												<Button
													text={t('restore')}
													size="small"
													layout="secondary"
													onClick={confirm(() => {
														const {
															restoreRevision,
														} = this.props

														restoreRevision(
															props.original.id
														)
														history.push(
															location.pathname
														)
													})}
												/>
											)}
										</Confirm>
									</FlexContainer>
								),
							},
						]}
					/>
				) : (
					<Text color="dark" weight="bold">
						{t('no_entries_found')}
					</Text>
				)}
			</>
		)
	}

	render() {
		const { revisions, revision, getRevision, t } = this.props
		const { preview } = this.state

		return (
			<PageOverlay
				location={location}
				contentWidth={!preview ? 'wide' : 'default'}
				noHorizontalSpacing={!!preview}
				headerBorder={!!preview}
				header={
					preview && revision.updatedAt ? (
						<FlexContainer wrap>
							<FlexContainer justifyContent="flex-start">
								<Avatar
									initials={
										revision.updatedBy.firstName.charAt(0) +
										revision.updatedBy.lastName.charAt(0)
									}
									image={revision.updatedBy.image}
								/>
								<Text color="dark" capitalize>
									<RelativeTime
										time={this.getValidTime(
											revision.updatedAt
										)}
									/>
									<br />
									<small className="u-max-width--small">
										{revision.updatedBy.firstName}{' '}
										{revision.updatedBy.lastName} ·{' '}
										{moment(
											this.getValidTime(
												revision.updatedAt
											)
										).format(t('date_and_time_format'))}
									</small>
								</Text>
							</FlexContainer>
							<FlexContainer
								justifyContent="flex-end"
								borderBetweenChilds
							>
								<>
									<Button
										icon={<Icon name="arrow-long-left" />}
										disabled={
											revisions[0].id === revision.id
										}
										onClick={() => {
											getRevision(
												revisions[
													revisions.findIndex(
														r =>
															r.id === revision.id
													) - 1
												].id
											)
										}}
									/>
									<Button
										icon={<Icon name="arrow-long-right" />}
										disabled={
											revisions[revisions.length - 1]
												.id === revision.id
										}
										onClick={() => {
											getRevision(
												revisions[
													revisions.findIndex(
														r =>
															r.id === revision.id
													) + 1
												].id
											)
										}}
									/>
								</>
								<FlexContainer justifyContent="flex-end">
									<FlexContainer
										justifyContent="flex-start"
										wrap
									>
										<Button
											text={t('back')}
											size="small"
											layout="ghosted"
											onClick={this.closePreview}
										/>
										<Confirm
											title={t('restore_revision')}
											text={
												<>
													<Text size="small">
														{t(
															'restore_revision_long'
														)}
													</Text>
													<Text color="dark">
														{revision.title &&
														revision.title.length
															? revision.title
															: t(
																	'without_title'
															  )}
														<br />
														<small>
															{
																revision
																	.updatedBy
																	.firstName
															}{' '}
															{
																revision
																	.updatedBy
																	.lastName
															}{' '}
															·{' '}
															{moment(
																this.getValidTime(
																	revision.updatedAt
																)
															).format(
																t(
																	'date_and_time_format'
																)
															)}
														</small>
													</Text>
													<Text size="small">
														{t(
															'restore_revision_notice'
														)}
													</Text>
												</>
											}
										>
											{confirm => (
												<Button
													text={t('restore')}
													size="small"
													layout="secondary"
													onClick={confirm(() => {
														const {
															restoreRevision,
														} = this.props

														restoreRevision(
															revision.id
														)
														history.push(
															location.pathname
														)
													})}
												/>
											)}
										</Confirm>
									</FlexContainer>
								</FlexContainer>
							</FlexContainer>
						</FlexContainer>
					) : null
				}
			>
				{preview ? this.renderPreview() : this.renderOverview()}
			</PageOverlay>
		)
	}
}

const mapStateToProps = state => ({
	articleId: state.articles.article.id,
	title: state.articles.article.title,
	revisions: state.revisions.list.toJSON(),
	revision: state.revisions.revision,
})

const mapDispatchToProps = {
	getRevisions: revisionActions.getRevisionsRequest,
	getRevision: revisionActions.getRevisionRequest,
	restoreRevision: revisionActions.restoreRevisionRequest,
}

export default withRouter(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(translate()(EditingHistoryPage))
)
